import axios from "axios";
import type {
  AdminUserOption,
  CreateTicketPayload,
  ReplyTicketPayload,
  SupportTicket,
  TicketFilterParams,
  TicketListResponse,
  TicketMessage,
  UpdateTicketStatusPayload,
} from "../types/supportTicket";

const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;

const buildQuery = (params: TicketFilterParams) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, String(value));
    }
  });

  return query.toString();
};

export const supportTicketService = {
  async createTicket(payload: CreateTicketPayload) {
    const formData = new FormData();
    formData.append("subject", payload.subject);
    formData.append("department", payload.department);
    formData.append("service", payload.service);
    formData.append("priority", payload.priority);
    formData.append("message", payload.message);
    formData.append("encrypted", String(payload.encrypted));

    payload.files?.forEach((file) => {
      formData.append("files", file);
    });

    const res = await axios.post(`${BASE}/support-tickets`, formData, {
      withCredentials: true,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return res.data;
  },

  async getAdminUsers(): Promise<AdminUserOption[]> {
    const res = await axios.get(`${BASE}/admin/support-tickets/admin-users`, {
      withCredentials: true,
    });
    return res.data;
  },

  async getSubscriberTickets(params: TicketFilterParams): Promise<TicketListResponse> {
    const query = buildQuery(params);
    const res = await axios.get(`${BASE}/support-tickets/my?${query}`, {
      withCredentials: true,
    });
    return res.data;
  },

  async getAdminTickets(params: TicketFilterParams): Promise<TicketListResponse> {
    const query = buildQuery(params);
    const res = await axios.get(`${BASE}/admin/support-tickets?${query}`, {
      withCredentials: true,
    });
    return res.data;
  },

  async getTicketById(id: string | number): Promise<SupportTicket> {
    const res = await axios.get(`${BASE}/support-tickets/${id}`, {
      withCredentials: true,
    });
    return res.data;
  },

  async getAdminTicketById(id: string | number): Promise<SupportTicket> {
    const res = await axios.get(`${BASE}/admin/support-tickets/${id}`, {
      withCredentials: true,
    });
    return res.data;
  },

  async getTicketMessages(id: string | number): Promise<TicketMessage[]> {
    const res = await axios.get(`${BASE}/support-tickets/${id}/messages`, {
      withCredentials: true,
    });
    return res.data;
  },

  async getAdminTicketMessages(id: string | number): Promise<TicketMessage[]> {
    const res = await axios.get(`${BASE}/admin/support-tickets/${id}/messages`, {
      withCredentials: true,
    });
    return res.data;
  },

  async replyToTicket(id: string | number, payload: ReplyTicketPayload) {
    const formData = new FormData();
    formData.append("message", payload.message);

    payload.files?.forEach((file) => {
      formData.append("files", file);
    });

    const res = await axios.post(`${BASE}/support-tickets/${id}/reply`, formData, {
      withCredentials: true,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return res.data;
  },

  async adminReplyToTicket(id: string | number, payload: ReplyTicketPayload) {
    const formData = new FormData();
    formData.append("message", payload.message);

    payload.files?.forEach((file) => {
      formData.append("files", file);
    });

    const res = await axios.post(`${BASE}/admin/support-tickets/${id}/reply`, formData, {
      withCredentials: true,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return res.data;
  },

  async updateTicketStatus(id: string | number, payload: UpdateTicketStatusPayload) {
    const res = await axios.patch(
      `${BASE}/admin/support-tickets/${id}/status`,
      payload,
      {
        withCredentials: true,
      }
    );
    return res.data;
  },
};